import { useShallow } from 'zustand/react/shallow'

import {
    OperationalPlannerFilter,
    selectAvailableByCountValue as selectOpAvailableByCountValue,
    selectAvailableDurationMaxFilter as selectOpAvailableDurationMaxFilter,
    selectAvailableDurationMinFilter as selectOpAvailableDurationMinFilter,
    selectLocationValues as selectOpLocationValues,
    selectOperationalPlannerFiltersForView as selectOperationalPlannerFilters,
    selectPractitionerValues as selectOpPractitionerValues,
    selectSurgeryTypeGroupValues as selectOpSurgeryTypeGroupValues,
    selectWeekdayValues as selectOpWeekdayValues,
} from '~/store/slices/operationalPlannerFilterSlice'
import {
    selectLocationValues as selectSurgeonLocationValues,
    selectPractitionerValues as selectSurgeonPractitionerValues,
    selectStatusValues as selectSurgeonStatusValues,
    selectSurgeonSchedulerFiltersForView,
    SurgeonSchedulerFilter,
} from '~/store/slices/surgeonSchedulerFilterSlice'
import {
    selectLocationValues as selectSsLocationValues,
    selectSurgerySchedulerFilters,
    SurgerySchedulerFilter,
} from '~/store/slices/surgerySchedulerFilterSlice'
import { FilterSliceKey } from '~/store/slices/types'
import {
    selectASAValues as selectWlASAValues,
    selectCancerValues as selectWlCancerValues,
    selectClearedValues as selectWlClearedValues,
    selectConfirmedValues as selectWlConfirmedValues,
    selectDayOvernightValues as selectWlDayOvernightValues,
    selectDiagnosisGroupValues as selectWlDiagnosisGroupValues,
    selectPatientAvailabilityFromFilter as selectWlPatientAvailabilityFromFilter,
    selectPatientAvailabilityToFilter as selectWlPatientAvailabilityToFilter,
    selectPractitionerComboValues as selectWlPractitionerComboValues,
    selectPrioritizationValues as selectWlPrioritizationValues,
    selectShortNoticeValues as selectWlShortNoticeValues,
    selectSurgeryCategoryValues as selectWlSurgeryCategoryValues,
    selectSurgeryLetterStatus as selectWlSurgeryLetterStatus,
    selectWaitingListFiltersForView as selectWaitingListFilters,
    selectWaitingValues as selectWlWaitingValues,
    WaitingListFilter,
} from '~/store/slices/waitingListFilterSlice'
import { StoreState, useStore } from '~/store/store'

import { BaseRoutesWithFilters, getPageTypeByPathname } from '../useViewOptionProfile'
import { createApplyFilterProfile, ToggleFilteredBySelectionFn } from './filterProfileUtils'

type AnyFilter = OperationalPlannerFilter | WaitingListFilter | SurgerySchedulerFilter | SurgeonSchedulerFilter

type FilterSelectors = {
    selectAllFilters: (state: StoreState) => AnyFilter[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: (state: StoreState) => any
}

type FilterActions = {
    toggleFilteredBy: (args: { value: string; label: string; type: string }) => void
    toggleFilteredBySelection: ToggleFilteredBySelectionFn
    removeAllFilterByType: (type: string) => void
    removeSingleFilter?: (id: string) => void
    emptyFilters: () => void
    applyFilterProfile: ReturnType<typeof createApplyFilterProfile>
}

export type FilterHookResult = FilterActions & {
    selectAllFilters: (state: StoreState) => AnyFilter[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any
}

const filterSelectorsByPath: Record<BaseRoutesWithFilters, FilterSelectors> = {
    'or-schedule': {
        selectAllFilters: selectOperationalPlannerFilters,
        selectAvailableDurationMinFilter: selectOpAvailableDurationMinFilter,
        selectAvailableDurationMaxFilter: selectOpAvailableDurationMaxFilter,
        selectAvailableByCountValue: selectOpAvailableByCountValue,
        selectPractitionerValues: selectOpPractitionerValues,
        selectSurgeryTypeGroupValues: selectOpSurgeryTypeGroupValues,
        selectWeekdayValues: selectOpWeekdayValues,
        selectLocationValues: selectOpLocationValues,
    },
    patients: {
        selectAllFilters: selectWaitingListFilters,
        selectASAValues: selectWlASAValues,
        selectClearedValues: selectWlClearedValues,
        selectConfirmedValues: selectWlConfirmedValues,
        selectDayOvernightValues: selectWlDayOvernightValues,
        selectDiagnosisGroupValues: selectWlDiagnosisGroupValues,
        selectCancerValues: selectWlCancerValues,
        selectPatientAvailabilityFilterFrom: selectWlPatientAvailabilityFromFilter,
        selectPatientAvailabilityFilterTo: selectWlPatientAvailabilityToFilter,
        selectPractitionerComboValues: selectWlPractitionerComboValues,
        selectPrioritizationValues: selectWlPrioritizationValues,
        selectShortNoticeValues: selectWlShortNoticeValues,
        selectSurgeryCategoryValues: selectWlSurgeryCategoryValues,
        selectSurgeryLetterStatus: selectWlSurgeryLetterStatus,
        selectWaitingValues: selectWlWaitingValues,
    },
    'surgery-scheduler': {
        selectAllFilters: selectSurgerySchedulerFilters,
        selectLocationValues: selectSsLocationValues,
    },
    'surgeon-scheduler': {
        selectAllFilters: selectSurgeonSchedulerFiltersForView,
        selectPractitionerValues: selectSurgeonPractitionerValues,
        selectLocationValues: selectSurgeonLocationValues,
        selectStatusValues: selectSurgeonStatusValues,
    },
}

const filterSliceKeysByPath: Record<BaseRoutesWithFilters, FilterSliceKey> = {
    'or-schedule': 'operationalPlannerFilters',
    patients: 'waitingListFilters',
    'surgery-scheduler': 'surgerySchedulerFilters',
    'surgeon-scheduler': 'surgeonSchedulerFilters',
}

export const createFilterSliceSelectorByPathname = (pathname: string) => {
    const pageType = getPageTypeByPathname(pathname)
    const sliceKey = filterSliceKeysByPath[pageType]
    return (state: StoreState) => state[sliceKey]
}

export const createFilterSliceActionsSelectorByPathname = (pathname: string) => (state: StoreState) => {
    const actions = createFilterSliceSelectorByPathname(pathname)(state).actions
    return {
        toggleFilteredBy: actions.toggleFilteredBy as (args: { value: string; label: string; type: string }) => void,
        toggleFilteredBySelection: actions.toggleFilteredBySelection as ToggleFilteredBySelectionFn,
        removeAllFilterByType: actions.removeAllFilterByType as (type: string) => void,
        removeSingleFilter: actions.removeSingleFilter as (id: string) => void,
        emptyFilters: actions.emptyFilters,
    }
}

export function useFiltersByPathname(pathname: string): FilterHookResult {
    const pageType = getPageTypeByPathname(pathname)
    const selectors = filterSelectorsByPath[pageType]

    const actions = useStore(useShallow(createFilterSliceActionsSelectorByPathname(pathname)))

    const toggleFilteredBySelection = actions.toggleFilteredBySelection as ToggleFilteredBySelectionFn
    const applyFilterProfile = createApplyFilterProfile(toggleFilteredBySelection)

    return {
        // Actions
        ...actions,
        applyFilterProfile,
        // Selectors
        ...selectors,
    }
}
